import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  Search,
  MoreHorizontal,
  Mail,
  Shield,
  Code,
  Palette,
  FileCode,
  CheckCircle,
  Lock,
  Unlock,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Eye,
  LayoutGrid,
  List,
  Calendar,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { generateUUID } from "@/utils/transform";

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    active: { label: "Active", className: "bg-emerald-500 text-white" },
    pending_verification: {
      label: "Pending",
      className: "bg-amber-500 text-white",
    },
    suspended: { label: "Suspended", className: "bg-red-500 text-white" },
    deleted: { label: "Deleted", className: "bg-gray-500 text-white" },
  };

  const { label, className } = config[status] || {
    label: status,
    className: "bg-gray-500 text-white",
  };

  return <Badge className={className}>{label}</Badge>;
};

// Role badge component
const RoleBadge = ({ role, isOwner }) => {
  const { t } = useTranslation();
  // If this user is the owner, show owner badge regardless of role
  if (isOwner) {
    return (
      <Badge className="bg-purple-600 text-white">{t("common.owner")}</Badge>
    );
  }

  const config = {
    admin: { label: "Admin", className: "bg-blue-600 text-white" },
    user: { label: "User", className: "bg-gray-200 text-gray-800" },
  };

  const { label, className } = config[role] || {
    label: role,
    className: "bg-gray-200 text-gray-800",
  };

  return <Badge className={className}>{label}</Badge>;
};

// User card component for grid view
const UserCard = ({ user, onView, onAction, currentUserId }) => {
  const { t } = useTranslation();
  const fullName =
    `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unnamed";
  const initials =
    `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() ||
    user.email?.[0]?.toUpperCase() ||
    "U";

  const renderLoggedIn = () => {
    if (user?.lastLoginAt) {
      return (
        <>Last login {new Date(user.lastLoginAt).toLocaleDateString("en-AU")}</>
      );
    }
    return user?.createdAt ? (
      <>Joined {new Date(user.createdAt).toLocaleDateString("en-AU")}</>
    ) : (
      <>{t("common.neverLoggedIn")}</>
    );
  };

  return (
    <Card className="group hover:shadow-md transition-shadow">
      {/* Header with avatar */}
      <div className="h-20 bg-primary/10 flex items-center justify-center relative">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="w-12 h-12 rounded-full"
            />
          ) : (
            <span className="text-lg font-semibold text-primary">
              {initials}
            </span>
          )}
        </div>

        {/* Actions dropdown */}
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onView(user)}>
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              {user.id !== currentUserId && (
                <>
                  {user.status === "suspended" ? (
                    <DropdownMenuItem
                      onClick={() => onAction("unsuspend", user)}
                    >
                      <Unlock className="mr-2 h-4 w-4" />
                      Unsuspend User
                    </DropdownMenuItem>
                  ) : (
                    user.status === "active" && (
                      <DropdownMenuItem
                        onClick={() => onAction("suspend", user)}
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        Suspend User
                      </DropdownMenuItem>
                    )
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onAction("delete", user)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete User
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status badge */}
        <div className="absolute bottom-2 left-2">
          <StatusBadge status={user.status} />
        </div>
      </div>

      {/* Content */}
      <CardContent className="p-4">
        <div className="mb-2">
          <h3 className="font-medium truncate">{fullName}</h3>
          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <RoleBadge role={user.role} isOwner={user.isOwner} />
        </div>

        {user.organisationName && (
          <p className="text-xs text-muted-foreground truncate mb-2">
            {user.organisationName}
          </p>
        )}

        <div className="pt-3 border-t text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {renderLoggedIn()}
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminUsers = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const [searchParams] = useSearchParams();

  // UI State
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [filters, setFilters] = useState({
    role: searchParams.get("role") || "",
    status: searchParams.get("status") || "",
    embedEnabled: searchParams.get("embedEnabled") || "",
  });

  // Dialog state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: "",
    user: null,
  });

  // Memoized query params
  const queryParams = useMemo(
    () => ({
      page,
      limit: 12,
      search: search || undefined,
      role: filters.role || undefined,
      status: filters.status || undefined,
      embedEnabled: filters.embedEnabled || undefined,
    }),
    [page, search, filters],
  );

  // Query: Fetch users list
  const {
    data: usersData,
    isLoading: loading,
    isFetching,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["admin", "users", queryParams],
    queryFn: async () => {
      const response = await adminApi.getUsers(queryParams);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error("Failed to load users");
    },
    placeholderData: (previousData) => previousData,
  });

  // Extract data from query
  const users = usersData?.users || [];
  const pagination = usersData?.pagination || {
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  };

  // Mutation: Toggle feature
  const toggleFeatureMutation = useMutation({
    mutationFn: async ({ userId, feature, enabled }) =>
      (await adminApi.toggleUserFeature(userId, feature, enabled)).data,
    onSuccess: (data, { enabled }) => {
      // Update selected user if viewing
      if (selectedUser?.id === data.data.id) {
        setSelectedUser(data.data);
      }
      toast.success(
        t("admin.users.featureToggled", {
          action: enabled
            ? t("admin.users.enabled")
            : t("admin.users.disabled"),
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: () => {
      toast.error(t("errors.updateFailed"));
    },
  });

  // Mutation: Enable all features
  const enableAllFeaturesMutation = useMutation({
    mutationFn: async (userId) =>
      (await adminApi.enableAllUserFeatures(userId)).data,
    onSuccess: (data) => {
      if (selectedUser?.id === data.data.id) {
        setSelectedUser(data.data);
      }
      toast.success(t("toast.allFeaturesEnabled"));
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: () => {
      toast.error(t("toast.failedEnableFeatures"));
    },
  });

  // Mutation: User actions (suspend, activate, delete, etc.)
  const userActionMutation = useMutation({
    mutationFn: ({ action, userId }) => {
      switch (action) {
        case "suspend":
          return adminApi.suspendUser(userId);
        case "activate":
          return adminApi.unsuspendUser(userId);
        case "delete":
          return adminApi.deleteUser(userId);
        case "verify-email":
          return adminApi.verifyUserEmail(userId);
        case "unlock":
          return adminApi.unlockUser(userId);
        default:
          throw new Error("Unknown action");
      }
    },
    onSuccess: (response, { action }) => {
      if (action === "delete") {
        toast.success(t("toast.userDeleted"));
      } else {
        toast.success(response?.data?.message || t("toast.actionCompleted"));
      }
      setConfirmDialog({ open: false, type: "", user: null });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err) => {
      toast.error(err.message || t("errors.actionFailed"));
    },
  });

  // Mutation: Change user role
  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }) => adminApi.updateUser(userId, { role }),
    onSuccess: (response) => {
      if (selectedUser?.id === response.data?.data?.id) {
        setSelectedUser(response.data.data);
      }
      toast.success(t("toast.roleUpdated"));
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err) => {
      toast.error(err.message || t("errors.updateFailed"));
    },
  });

  // Combined action loading state
  const actionLoading =
    toggleFeatureMutation.isPending ||
    enableAllFeaturesMutation.isPending ||
    userActionMutation.isPending ||
    changeRoleMutation.isPending;

  // Handler: Toggle feature
  const toggleFeature = (userId, feature, enabled) => {
    toggleFeatureMutation.mutate({ userId, feature, enabled });
  };

  // Handler: Enable all features
  const enableAllFeatures = (userId) => {
    enableAllFeaturesMutation.mutate(userId);
  };

  // Handler: Perform user action
  const performUserAction = (action, userId) => {
    userActionMutation.mutate({ action, userId });
  };

  // Handler: Change user role
  const changeUserRole = (userId, newRole) => {
    changeRoleMutation.mutate({ userId, role: newRole });
  };

  const renderUsersGridOrList = () => {
    if (loading) {
      return (
        <Card>
          <CardContent className="pt-6 space-y-4">
            {[1, 2, 3, 4, 5].map(() => (
              <Skeleton key={generateUUID()} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      );
    }
    if (users?.length === 0) {
      return (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t("admin.noUsersFound")}</p>
            <p className="text-muted-foreground">
              {t("common.tryAdjustingFilters")}
            </p>
          </CardContent>
        </Card>
      );
    }

    if (viewMode === "grid") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {users.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              currentUserId={currentUser?.id}
              onView={(u) => {
                setSelectedUser(u);
                setUserDetailOpen(true);
              }}
              onAction={(type, u) => {
                if (type === "suspend" || type === "delete") {
                  setConfirmDialog({ open: true, type, user: u });
                } else if (type === "unsuspend") {
                  performUserAction("activate", u.id);
                }
              }}
            />
          ))}
        </div>
      );
    }

    return (
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.user")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("common.features")}</TableHead>
                <TableHead>{t("common.lastLogin")}</TableHead>
                <TableHead className="w-20">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt=""
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <span className="text-sm font-medium">
                            {(user.firstName?.[0] || "") +
                              (user.lastName?.[0] ||
                                user.email[0].toUpperCase())}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={user.role} isOwner={user.isOwner} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={user.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {user.embedEnabled && (
                        <Badge variant="outline" className="text-xs">
                          <Code className="h-3 w-3 mr-1" />
                          Embed
                        </Badge>
                      )}
                      {user.whiteLabelEnabled && (
                        <Badge variant="outline" className="text-xs">
                          <Palette className="h-3 w-3 mr-1" />
                          WL
                        </Badge>
                      )}
                      {user.customCssEnabled && (
                        <Badge variant="outline" className="text-xs">
                          <FileCode className="h-3 w-3 mr-1" />
                          CSS
                        </Badge>
                      )}
                      {!user.embedEnabled &&
                        !user.whiteLabelEnabled &&
                        !user.customCssEnabled && (
                          <span className="text-muted-foreground text-sm">
                            —
                          </span>
                        )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.lastLoginAt ? (
                      <span className="text-sm text-muted-foreground">
                        {new Date(user.lastLoginAt).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {t("common.never")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>
                          {t("common.actions")}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setUserDetailOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => enableAllFeatures(user.id)}
                        >
                          <Code className="h-4 w-4 mr-2" />
                          Enable All Features
                        </DropdownMenuItem>
                        {!user.emailVerified && (
                          <DropdownMenuItem
                            onClick={() =>
                              performUserAction("verify-email", user.id)
                            }
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            Verify Email
                          </DropdownMenuItem>
                        )}
                        {user.lockedUntil && (
                          <DropdownMenuItem
                            onClick={() => performUserAction("unlock", user.id)}
                          >
                            <Unlock className="h-4 w-4 mr-2" />
                            Unlock Account
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {user.status === "active" ? (
                          <DropdownMenuItem
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                type: "suspend",
                                user,
                              })
                            }
                            className="text-amber-600"
                          >
                            <Lock className="h-4 w-4 mr-2" />
                            Suspend User
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() =>
                              performUserAction("activate", user.id)
                            }
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Activate User
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() =>
                            setConfirmDialog({
                              open: true,
                              type: "delete",
                              user,
                            })
                          }
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderButtonTitle = () => {
    if (actionLoading) {
      return "Processing...";
    }
    return confirmDialog?.type === "delete" ? "Delete" : "Suspend";
  };

  // Check owner access
  if (!currentUser?.isOwner) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t("errors.accessDenied")}
            </h2>
            <p className="text-muted-foreground">
              This page is only accessible to the app owner.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("admin.userManagement")}
          </h1>
          <p className="text-muted-foreground">
            Manage users, features, and permissions
          </p>
        </div>
        <Button
          onClick={() => refetchUsers()}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw
            className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("placeholders.searchByNameOrEmail")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            <Select
              value={filters.role}
              onValueChange={(v) => {
                setFilters({ ...filters, role: v === "all" ? "" : v });
                setPage(1);
              }}
            >
              <SelectTrigger className="w-35">
                <SelectValue placeholder={t("team.role")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("admin.users.allRoles")}</SelectItem>
                <SelectItem value="user">{t("team.roles.user")}</SelectItem>
                <SelectItem value="admin">{t("team.roles.admin")}</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(v) => {
                setFilters({ ...filters, status: v === "all" ? "" : v });
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t("common.status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("admin.invoices.allStatus")}
                </SelectItem>
                <SelectItem value="active">{t("common.active")}</SelectItem>
                <SelectItem value="pending_verification">
                  {t("common.pending")}
                </SelectItem>
                <SelectItem value="suspended">
                  {t("admin.users.suspended")}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.embedEnabled}
              onValueChange={(v) => {
                setFilters({ ...filters, embedEnabled: v === "all" ? "" : v });
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t("admin.users.embed")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("admin.users.allUsers")}</SelectItem>
                <SelectItem value="true">
                  {t("admin.users.embedEnabled")}
                </SelectItem>
                <SelectItem value="false">
                  {t("admin.users.embedDisabled")}
                </SelectItem>
              </SelectContent>
            </Select>

            {/* View toggle */}
            <div className="flex items-center border rounded-lg p-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Grid/Table */}
      {renderUsersGridOrList()}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pagination.limit + 1} to{" "}
            {Math.min(page * pagination.limit, pagination.total)} of{" "}
            {pagination.total} users
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((prev) => prev - 1)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from(
                { length: Math.min(5, pagination.totalPages) },
                (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8 p-0"
                      disabled={isFetching}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                },
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages || isFetching}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("admin.userDetails")}</DialogTitle>
            <DialogDescription>View and manage user settings</DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-xl font-medium">
                  {selectedUser.avatarUrl ? (
                    <img
                      src={selectedUser.avatarUrl}
                      alt=""
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    (selectedUser.firstName?.[0] || "") +
                    (selectedUser.lastName?.[0] ||
                      selectedUser.email[0].toUpperCase())
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                  <div className="flex gap-2 mt-2">
                    <RoleBadge
                      role={selectedUser.role}
                      isOwner={selectedUser.isOwner}
                    />
                    <StatusBadge status={selectedUser.status} />
                  </div>
                </div>
              </div>

              {/* Role Change - owner can change user/admin roles (but not owner status) */}
              {!selectedUser.isOwner && selectedUser.id !== currentUser.id && (
                <div className="space-y-2">
                  <Label>{t("admin.userRole")}</Label>
                  <Select
                    value={selectedUser.role}
                    onValueChange={(v) => changeUserRole(selectedUser.id, v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">{t("common.user")}</SelectItem>
                      <SelectItem value="admin">{t("nav.admin")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Note: Owner status is configured via environment variable,
                    not database role.
                  </p>
                </div>
              )}

              {/* Features */}
              <div className="space-y-4">
                <h4 className="font-medium">{t("common.features")}</h4>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Code className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium">{t("embed.integration")}</p>
                        <p className="text-sm text-muted-foreground">
                          Allow embedding eScribe in external applications
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={selectedUser.embedEnabled}
                      onCheckedChange={(v) =>
                        toggleFeature(selectedUser.id, "embed", v)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Palette className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="font-medium">
                          {t("settings.whiteLabel")}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Remove eScribe branding from embeds
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={selectedUser.whiteLabelEnabled}
                      onCheckedChange={(v) =>
                        toggleFeature(selectedUser.id, "whiteLabel", v)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileCode className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className="font-medium">{t("embed.customCss")}</p>
                        <p className="text-sm text-muted-foreground">
                          Apply custom styles to embed interface
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={selectedUser.customCssEnabled}
                      onCheckedChange={(v) =>
                        toggleFeature(selectedUser.id, "customCss", v)
                      }
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => enableAllFeatures(selectedUser.id)}
                    disabled={actionLoading}
                  >
                    Enable All
                  </Button>
                </div>
              </div>

              {/* Account Info */}
              <div className="space-y-2 text-sm">
                <h4 className="font-medium">{t("settings.accountInfo")}</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <span className="ml-2">
                      {new Date(selectedUser.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Login:</span>
                    <span className="ml-2">
                      {selectedUser.lastLoginAt
                        ? new Date(selectedUser.lastLoginAt).toLocaleString()
                        : "Never"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Email Verified:
                    </span>
                    <span className="ml-2">
                      {selectedUser.emailVerified ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">2FA:</span>
                    <span className="ml-2">
                      {selectedUser.twoFactorEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(v) =>
          !v && setConfirmDialog({ open: false, type: "", user: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Action
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.type === "suspend" && (
                <>
                  Are you sure you want to suspend {confirmDialog.user?.email}?
                  They will not be able to access their account.
                </>
              )}
              {confirmDialog.type === "delete" && (
                <>
                  Are you sure you want to delete {confirmDialog.user?.email}?
                  This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({ open: false, type: "", user: null })
              }
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant={
                confirmDialog.type === "delete" ? "destructive" : "default"
              }
              onClick={() =>
                performUserAction(confirmDialog.type, confirmDialog.user?.id)
              }
              disabled={actionLoading}
            >
              {renderButtonTitle()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
